/**
 * MarqueeForm Component
 * Form for creating and editing marquee announcements
 */

import { useState, useEffect } from 'react';
import type { MarqueeAdmin, CreateMarqueeDTO, UpdateMarqueeDTO } from '../../types';
import MarqueePreview from './MarqueePreview';
import './MarqueeForm.css';

interface MarqueeFormProps {
  initialData?: MarqueeAdmin;
  onSubmit: (data: CreateMarqueeDTO | UpdateMarqueeDTO) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  defaultSpeed?: number;
}

const DEFAULT_VALUES = {
  textColor: '#000000',
  bgColor: '#FFFF00',
  bgOpacity: 1.0,
  isActive: true,
} as const;

const MAX_CONTENT_LENGTH = 100;

const MarqueeForm: React.FC<MarqueeFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  defaultSpeed = 15,
}) => {
  const [formData, setFormData] = useState({
    content: initialData?.content || '',
    textColor: initialData?.textColor || DEFAULT_VALUES.textColor,
    bgColor: initialData?.bgColor || DEFAULT_VALUES.bgColor,
    bgOpacity: initialData?.bgOpacity ?? DEFAULT_VALUES.bgOpacity,
    isActive: initialData?.isActive ?? DEFAULT_VALUES.isActive,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        content: initialData.content || '',
        textColor: initialData.textColor || DEFAULT_VALUES.textColor,
        bgColor: initialData.bgColor || DEFAULT_VALUES.bgColor,
        bgOpacity: initialData.bgOpacity ?? DEFAULT_VALUES.bgOpacity,
        isActive: initialData.isActive ?? DEFAULT_VALUES.isActive,
      });
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'range') {
      const numValue = parseFloat(value);
      setFormData((prev) => ({ ...prev, [name]: numValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.content.trim()) {
      newErrors.content = '內容是必填欄位';
    } else if (formData.content.length > MAX_CONTENT_LENGTH) {
      newErrors.content = `內容最多 ${MAX_CONTENT_LENGTH} 字`;
    }

    // Validate hex color format
    const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
    if (!hexColorRegex.test(formData.textColor)) {
      newErrors.textColor = '請輸入有效的顏色代碼 (例如: #000000)';
    }
    if (!hexColorRegex.test(formData.bgColor)) {
      newErrors.bgColor = '請輸入有效的顏色代碼 (例如: #FFFF00)';
    }

    // Validate opacity range
    if (formData.bgOpacity < 0 || formData.bgOpacity > 1) {
      newErrors.bgOpacity = '透明度必須在 0.0 到 1.0 之間';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formId = 'marquee-form';

  return (
    <div className="marquee-form">
      <form id={formId} onSubmit={handleSubmit}>
        {/* Content */}
        <div className="marquee-form__field">
          <label htmlFor="content" className="marquee-form__label">
            文字內容 *
          </label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            className={`marquee-form__textarea ${
              errors.content ? 'marquee-form__textarea--error' : ''
            }`}
            placeholder="請輸入跑馬燈文字內容，支援 Emoji 🎉"
            rows={3}
            disabled={isSubmitting || isLoading}
            maxLength={MAX_CONTENT_LENGTH}
          />
          <div className="marquee-form__char-count">
            {formData.content.length} / {MAX_CONTENT_LENGTH}
          </div>
          {errors.content && (
            <span className="marquee-form__error">{errors.content}</span>
          )}
        </div>

        {/* Colors Section */}
        <div className="marquee-form__section">
          <h4 className="marquee-form__section-title">顏色設定</h4>

          <div className="marquee-form__row">
            <div className="marquee-form__field">
              <label htmlFor="textColor" className="marquee-form__label">
                文字顏色
              </label>
              <div className="marquee-form__color-input">
                <input
                  type="color"
                  id="textColor-color"
                  name="textColor"
                  value={formData.textColor}
                  onChange={handleChange}
                  className="marquee-form__color-picker"
                  disabled={isSubmitting || isLoading}
                />
                <input
                  type="text"
                  id="textColor"
                  name="textColor"
                  value={formData.textColor}
                  onChange={handleChange}
                  className={`marquee-form__input ${
                    errors.textColor ? 'marquee-form__input--error' : ''
                  }`}
                  disabled={isSubmitting || isLoading}
                  maxLength={7}
                />
              </div>
              {errors.textColor && (
                <span className="marquee-form__error">{errors.textColor}</span>
              )}
            </div>

            <div className="marquee-form__field">
              <label htmlFor="bgColor" className="marquee-form__label">
                背景顏色
              </label>
              <div className="marquee-form__color-input">
                <input
                  type="color"
                  id="bgColor-color"
                  name="bgColor"
                  value={formData.bgColor}
                  onChange={handleChange}
                  className="marquee-form__color-picker"
                  disabled={isSubmitting || isLoading}
                />
                <input
                  type="text"
                  id="bgColor"
                  name="bgColor"
                  value={formData.bgColor}
                  onChange={handleChange}
                  className={`marquee-form__input ${
                    errors.bgColor ? 'marquee-form__input--error' : ''
                  }`}
                  disabled={isSubmitting || isLoading}
                  maxLength={7}
                />
              </div>
              {errors.bgColor && (
                <span className="marquee-form__error">{errors.bgColor}</span>
              )}
            </div>
          </div>

          <div className="marquee-form__field">
            <label htmlFor="bgOpacity" className="marquee-form__label">
              背景透明度: {formData.bgOpacity.toFixed(1)}
            </label>
            <input
              type="range"
              id="bgOpacity"
              name="bgOpacity"
              value={formData.bgOpacity}
              onChange={handleChange}
              className="marquee-form__slider"
              min={0}
              max={1}
              step={0.1}
              disabled={isSubmitting || isLoading}
            />
            <div className="marquee-form__slider-labels">
              <span>0.0 (透明)</span>
              <span>1.0 (不透明)</span>
            </div>
            {errors.bgOpacity && (
              <span className="marquee-form__error">{errors.bgOpacity}</span>
            )}
          </div>
        </div>

        {/* Display Settings */}
        <div className="marquee-form__section">
          <h4 className="marquee-form__section-title">顯示設定</h4>

          <div className="marquee-form__field marquee-form__field--checkbox">
            <label className="marquee-form__checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="marquee-form__checkbox"
                disabled={isSubmitting || isLoading}
              />
              <span>啟用此跑馬燈</span>
            </label>
          </div>
        </div>

        {/* Preview Section */}
        <div className="marquee-form__section">
          <h4 className="marquee-form__section-title">即時預覽</h4>
          <MarqueePreview
            content={formData.content}
            textColor={formData.textColor}
            bgColor={formData.bgColor}
            bgOpacity={formData.bgOpacity}
            defaultSpeed={defaultSpeed}
          />
        </div>
      </form>

      {/* Actions */}
      <div className="marquee-form__actions">
        <button
          type="button"
          onClick={onCancel}
          className="marquee-form__button marquee-form__button--outline"
          disabled={isSubmitting || isLoading}
        >
          取消
        </button>
        <button
          type="submit"
          form={formId}
          className="marquee-form__button marquee-form__button--primary"
          disabled={isSubmitting || isLoading}
        >
          {isSubmitting ? '儲存中...' : initialData ? '更新跑馬燈' : '新增跑馬燈'}
        </button>
      </div>
    </div>
  );
};

export default MarqueeForm;
